import styled from 'styled-components';

export const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

export const TabButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 0.5rem 0.5rem 0 0;
  transition: all 0.2s ease;
  color: ${props => props.active ? '#ffffff' : '#64748b'};
  background-color: ${props => props.active ? '#2563eb' : 'transparent'};
  border-bottom: ${props => props.active ? '2px solid #2563eb' : 'none'};
  
  &:hover {
    background-color: ${props => props.active ? '#1d4ed8' : '#f1f5f9'};
    color: ${props => props.active ? '#ffffff' : '#1e293b'};
  }
`;

export const FormContainer = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

export const FormHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
`;

export const FormTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

export const FormDescription = styled.p`
  color: #64748b;
  font-size: 0.9rem;
`;

export const FormContent = styled.div`
  padding: 1.5rem;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const Label = styled.label`
  font-weight: 500;
  color: #1e293b;
  font-size: 0.9rem;
`;

export const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: white;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: white;
  cursor: pointer;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    outline: none;
  }
`;

export const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: white;
  resize: vertical;
  min-height: 100px;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.75rem;
  cursor: pointer;
  border: none;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: #2563eb;
          color: white;
          &:hover {
            background-color: #1d4ed8;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
        `;
      case 'success':
        return `
          background-color: #10b981;
          color: white;
          &:hover {
            background-color: #059669;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
        `;
      case 'danger':
        return `
          background-color: #ef4444;
          color: white;
          &:hover {
            background-color: #dc2626;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: #2563eb;
          border: 1px solid #2563eb;
          &:hover {
            background-color: #2563eb;
            color: white;
          }
        `;
      default:
        return `
          background-color: #f1f5f9;
          color: #1e293b;
          border: 1px solid #e2e8f0;
          &:hover {
            background-color: #e2e8f0;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const TableHeader = styled.thead`
  background-color: #f8fafc;
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid #e2e8f0;
  
  &:hover {
    background-color: #f8fafc;
  }
`;

export const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 2px solid #e2e8f0;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

export const TableCell = styled.td`
  padding: 1rem;
  color: #64748b;
  vertical-align: middle;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

export const Grid = styled.div`
  display: grid;
  gap: 2rem;
  
  ${props => {
    switch (props.columns) {
      case 2:
        return 'grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));';
      case 3:
        return 'grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));';
      default:
        return 'grid-template-columns: 1fr;';
    }
  }}

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background-color: rgba(16, 185, 129, 0.1);
          color: #10b981;
        `;
      case 'inactive':
        return `
          background-color: rgba(100, 116, 139, 0.1);
          color: #64748b;
        `;
      case 'pending':
        return `
          background-color: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        `;
      default:
        return `
          background-color: #f8fafc;
          color: #64748b;
        `;
    }
  }}
`;

export const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 0.5rem;
  }
`;

export const CardHeader = styled.header`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

export const CardDescription = styled.p`
  color: #64748b;
  font-size: 0.9rem;
`;

export const CardContent = styled.div`
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

export const PageHeader = styled.header`
  margin-bottom: 2rem;
`;

export const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.secondary};
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

export const PageSubtitle = styled.p`
  color: #64748b;
  font-size: 1.1rem;
`;

export const ScrollableContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

export const FixedHeaderTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  
  .table-header {
    background-color: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .table-body {
    max-height: 350px;
    overflow-y: auto;
    
    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
      
      &:hover {
        background: #94a3b8;
      }
    }
    
    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
  }
`; 